import React, { FC, Fragment, useState } from 'react';
import { Button, ListItem, ListItemText, ListItemButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PropertyList } from 'components/property-settings/property-shared-styles';
import { MaterialMacro, MaterialMacroOption } from 'models/material-macro';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type MaterialMacroDialogProps = {
    onClose: () => void;
    open: boolean;
    onAddMaterialMacro: (selectedMaterialMacro?: MaterialMacro) => void;
    // Every macro linked to the product. The unusable ones render disabled with the reason,
    // so the lab can see what is configured and why it cannot be applied.
    materialMacroOptions: MaterialMacroOption[];
    // Names the product being asked about, for prompts shown detached from the grid row.
    actionLabel?: string;
};
const MaterialMacroDialog: FC<MaterialMacroDialogProps> = ({
    onClose,
    open,
    onAddMaterialMacro,
    materialMacroOptions,
    actionLabel,
}: MaterialMacroDialogProps) => {
    const { t: translate } = useTranslation(['actions-materials', 'common']);
    const [selectedMaterialMacro, setSelectedMaterialMacro] = useState<MaterialMacro>();

    const handleListItemClick = (materialMacro: MaterialMacro) => {
        setSelectedMaterialMacro(materialMacro);
    };

    const handleClick = () => {
        onAddMaterialMacro(selectedMaterialMacro);
        setSelectedMaterialMacro(undefined);
        onClose();
    };

    // With nothing selectable there is no choice to force, so the same button becomes a plain
    // Close. It still runs handleClick with no selection, which every caller already treats as
    // "no macro chosen" - that keeps each flow resolving through its existing path.
    const hasSelectableMacro = materialMacroOptions.some((materialMacroOption) => materialMacroOption.isSelectable);

    return (
        <Fragment>
            <DHDialog
                title={translate('actions-materials:SELECT_MATERIAL_MACRO')}
                maxWidth="sm"
                fullWidth
                open={open}
                // Mandatory while a macro can be picked; once nothing can be, the X and
                // backdrop resolve the step exactly as the Close button does. Routed through
                // handleClick, never the raw onClose: the flows advance on onAddMaterialMacro,
                // so closing without it would leave the post-save queue stuck open.
                onClose={hasSelectableMacro ? () => undefined : handleClick}
                actions={
                    <Button disabled={hasSelectableMacro && !selectedMaterialMacro?._id} onClick={handleClick}>
                        {hasSelectableMacro ? translate('common:ADD') : translate('common:CLOSE')}
                    </Button>
                }
            >
                {actionLabel ? (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {translate('actions-materials:FOR_PRODUCT', {
                            product: actionLabel,
                            // React escapes the text; i18next's escaping would show a "/" as &#x2F;.
                            interpolation: { escapeValue: false },
                        })}
                    </Typography>
                ) : undefined}
                <PropertyList>
                    {materialMacroOptions?.map(({ macro, isSelectable, missingMaterialNames }) => {
                        return (
                            <ListItem
                                key={macro._id}
                                disableGutters
                                // PropertyList is a wrapping flex row - a disabled row carries a
                                // long explanation, so give it the full width to stay readable.
                                sx={isSelectable ? undefined : { width: '100%' }}
                            >
                                <ListItemButton
                                    disabled={!isSelectable}
                                    onClick={() => handleListItemClick(macro)}
                                    sx={{
                                        outline: selectedMaterialMacro?._id === macro._id ? '2px solid #999' : 'none',
                                        // MUI dims a disabled button to 38% opacity, and CSS parent
                                        // opacity cannot be undone by a child - the explanation would
                                        // be washed out. Drop it and convey the disabled state with
                                        // color instead; `disabled` still blocks the click and sets
                                        // aria-disabled.
                                        '&.Mui-disabled': { opacity: 1 },
                                    }}
                                    selected={selectedMaterialMacro?._id === macro._id}
                                >
                                    <ListItemText
                                        primary={macro.name}
                                        primaryTypographyProps={isSelectable ? undefined : { color: 'text.disabled' }}
                                        secondary={
                                            isSelectable
                                                ? undefined
                                                : translate('actions-materials:MACRO_MATERIALS_NOT_IN_PRICE_LIST', {
                                                      materials: missingMaterialNames.join(', '),
                                                  })
                                        }
                                        secondaryTypographyProps={{ color: 'warning.main' }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </PropertyList>
            </DHDialog>
        </Fragment>
    );
};

export default MaterialMacroDialog;
